import { api } from '@/api/client';
import { defineQuery } from '@/api/resource';
import type { User } from '@/lib/types';

/**
 * The signed-in user.
 *
 * Declared once and read from four places that each used to spell it out:
 * `useMe`, `requireSession`, `requireCapability`/`requireGlobalCapability` (via
 * `getQueryData`), and `routes/login.tsx` — the last of which was a verbatim
 * copy of `requireSession`'s four lines rather than a call to it.
 *
 * The envelope is stripped INSIDE `request`, not with `unwrap`. That looks like
 * an inconsistency and is not: `unwrap` compiles to `select`, which transforms
 * what a component sees while leaving the envelope in the cache — but this slot
 * is written directly by `LoginPage` (`setQueryData(k.session.me, data.user)`)
 * and read directly by the route guards (`getQueryData<User>`). Both handle the
 * bare `User`. Moving the unwrap out would put `{ user }` in the cache for the
 * fetch path and `User` for the login path, and since `setQueryData` is not
 * typed by key, nothing would complain until a guard read `.role` off undefined
 * at runtime.
 */
export const sessionMe = defineQuery({
  namespace: 'session',
  name: 'me',
  domains: ['identity_rbac'],
  keyOf: () => [],
  request: (_: void, signal) => api.get<{ user: User }>('/me', { signal }).then((r) => r.user),
});
